import { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';

// ── Login Page ────────────────────────────────────────────────
export function Login() {
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const { login }  = useAuth();
  const navigate   = useNavigate();

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight:'100vh', background:'var(--navy)', display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
      <div style={{ background:'var(--white)', borderRadius:'var(--radius-lg)', padding:48, width:'100%', maxWidth:420, boxShadow:'var(--shadow-lg)' }}>
        <div style={{ textAlign:'center', marginBottom:32 }}>
          <div style={{ fontSize:'2.5rem', marginBottom:8 }}>🐾</div>
          <h1 style={{ fontFamily:'var(--font-display)', fontSize:'1.6rem', color:'var(--navy)', marginBottom:4 }}>Admin Login</h1>
          <p style={{ fontFamily:'var(--font-ui)', fontSize:'0.85rem', color:'var(--text-light)' }}>Run Around Dog Town Rescue</p>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-control" type="email" required value={email} onChange={e => setEmail(e.target.value)} autoFocus />
          </div>
          <div className="form-group">
            <label className="form-label">Password</label>
            <input className="form-control" type="password" required value={password} onChange={e => setPassword(e.target.value)} />
          </div>
          <button type="submit" className="btn btn-primary" style={{ width:'100%', justifyContent:'center', marginTop:8 }} disabled={loading}>
            {loading ? 'Logging in…' : 'Log In'}
          </button>
        </form>

        <div style={{ textAlign:'center', marginTop:20 }}>
          <Link to="/forgot-password" style={{ fontFamily:'var(--font-ui)', fontSize:'0.875rem', color:'var(--text-light)' }}>
            Forgot your password?
          </Link>
        </div>

        <div style={{ textAlign:'center', marginTop:24 }}>
          <Link to="/" style={{ fontFamily:'var(--font-ui)', fontSize:'0.8rem', color:'var(--text-light)' }}>← Back to website</Link>
        </div>
      </div>
    </div>
  );
}

// ── Forgot Password ───────────────────────────────────────────
export function ForgotPassword() {
  const [email,     setEmail]     = useState('');
  const [loading,   setLoading]   = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error,     setError]     = useState('');

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.forgotPassword(email);
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight:'100vh', background:'var(--navy)', display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
      <div style={{ background:'var(--white)', borderRadius:'var(--radius-lg)', padding:48, width:'100%', maxWidth:420, boxShadow:'var(--shadow-lg)' }}>
        <div style={{ textAlign:'center', marginBottom:32 }}>
          <div style={{ fontSize:'2.5rem', marginBottom:8 }}>🔑</div>
          <h1 style={{ fontFamily:'var(--font-display)', fontSize:'1.5rem', color:'var(--navy)' }}>Reset Password</h1>
        </div>

        {submitted ? (
          <div className="alert alert-success">
            Check your email — if an account exists for that address, a reset link has been sent.
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && <div className="alert alert-error">{error}</div>}
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input className="form-control" type="email" required value={email} onChange={e => setEmail(e.target.value)} autoFocus />
            </div>
            <button type="submit" className="btn btn-primary" style={{ width:'100%', justifyContent:'center' }} disabled={loading}>
              {loading ? 'Sending…' : 'Send Reset Link'}
            </button>
          </form>
        )}

        <div style={{ textAlign:'center', marginTop:20 }}>
          <Link to="/admin" style={{ fontFamily:'var(--font-ui)', fontSize:'0.875rem', color:'var(--text-light)' }}>← Back to login</Link>
        </div>
      </div>
    </div>
  );
}

// ── Reset Password ────────────────────────────────────────────
export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token    = searchParams.get('token') || '';
  const email    = searchParams.get('email') || '';
  const navigate = useNavigate();

  const [password,  setPassword]  = useState('');
  const [password2, setPassword2] = useState('');
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');

  const handleSubmit = async e => {
    e.preventDefault();
    if (password !== password2) { setError('Passwords do not match'); return; }
    if (password.length < 8)    { setError('Password must be at least 8 characters'); return; }
    setError('');
    setLoading(true);
    try {
      await api.resetPassword({ email, token, password });
      navigate('/admin?reset=1');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight:'100vh', background:'var(--navy)', display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
      <div style={{ background:'var(--white)', borderRadius:'var(--radius-lg)', padding:48, width:'100%', maxWidth:420, boxShadow:'var(--shadow-lg)' }}>
        <div style={{ textAlign:'center', marginBottom:32 }}>
          <div style={{ fontSize:'2.5rem', marginBottom:8 }}>🔐</div>
          <h1 style={{ fontFamily:'var(--font-display)', fontSize:'1.5rem', color:'var(--navy)' }}>Set New Password</h1>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        {!token ? (
          <div className="alert alert-error">Invalid reset link. Please request a new one.</div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">New Password</label>
              <input className="form-control" type="password" required minLength={8} value={password} onChange={e => setPassword(e.target.value)} autoFocus />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm New Password</label>
              <input className="form-control" type="password" required value={password2} onChange={e => setPassword2(e.target.value)} />
            </div>
            <button type="submit" className="btn btn-primary" style={{ width:'100%', justifyContent:'center' }} disabled={loading}>
              {loading ? 'Saving…' : 'Set New Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
